/**
 * OCT28 #12 (field test 2026-10-28) — a placement the database refused at commit (PT409,
 * PLACEMENT_REVALIDATION_FAILED: <reason>) is said in words the dispatcher can act on, and OptiPlace refreshes its
 * recommendations instead of leaving a generic "Error Creating Ticket" with SQL codes. The refusal stays HARD: nothing
 * was created, and the browser cannot override the guard.
 */
export type PlacementRefusalReason =
  | 'TECHNICIAN_INTERVAL_CONFLICT' | 'BAY_NOT_AVAILABLE' | 'TECHNICIAN_NOT_ELIGIBLE'
  | 'TECHNICIAN_NOT_SCHEDULED' | 'TECHNICIAN_ON_TIME_OFF';

const WORDS: Record<PlacementRefusalReason, string> = {
  TECHNICIAN_INTERVAL_CONFLICT: 'That time is no longer free for this technician — other work now occupies it.',
  BAY_NOT_AVAILABLE: 'That bay is no longer available at that time (occupied, locked out or inactive).',
  TECHNICIAN_NOT_ELIGIBLE: 'This technician can no longer take new work (not present or not active).',
  TECHNICIAN_NOT_SCHEDULED: 'This technician is not scheduled to work at that time.',
  TECHNICIAN_ON_TIME_OFF: 'This technician is on time off that day.',
};

/** The guard's reason when the error is a placement refusal; otherwise null. */
export function placementRefusalReason(error: unknown): PlacementRefusalReason | null {
  const e = (error ?? {}) as { code?: string; message?: string; details?: string };
  const fromDetail = String(e.details ?? '').trim();
  const fromMessage = /PLACEMENT_REVALIDATION_FAILED:\s*([A-Z_]+)/.exec(String(e.message ?? ''))?.[1] ?? '';
  if (e.code !== 'PT409' && !fromMessage) return null;
  const reason = (fromDetail in WORDS ? fromDetail : fromMessage) as PlacementRefusalReason;
  return reason in WORDS ? reason : null;
}

/** Toast copy: what happened, that nothing was created, and that the recommendations were refreshed. */
export function placementRefusalMessage(reason: PlacementRefusalReason): { title: string; description: string } {
  return {
    title: 'Schedule changed — nothing was created',
    description: `${WORDS[reason]} OptiPlace has refreshed its recommendations; choose again.`,
  };
}
